import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
  useCallback,
} from 'react'
import { Image } from 'src/components/ui/Image'
import { useMobile } from 'src/hooks/useMobile'

import type { ArrowStyle } from './Arrows'
import './Carousel.scss'
import type { Video } from '../../product/sections/GalleryImage/types/gallery-image'
import { useCarouselDrag } from './useCarouselDrag'
import CarouselControls from './CarouselControls'
import { useCarouselState } from './useCarouselState'
import { CarouselSlide } from './CarouselSlide'

export type Image = {
  imageUrl: string
  imageText: string
  imageAlt?: string
}

export type CarouselItems = Array<Image | Video>

type CarouselProps = {
  items: CarouselItems
  width: number
  height: number
  slideWidthPercentage?: number
  alignLastSlideToEnd?: boolean
  sizes?: string
  srcSetUnit?: 'x' | 'w'
  dots?: boolean
  dotsVariant?: 'default' | 'product-card'
  arrows?: boolean
  arrowStyle?: ArrowStyle
  enableDrag?: boolean
  initialSlide?: number
  href?: string
  Flags?: React.ComponentType
  sectionClassName?: string
  onSlideChange?: (currentIndex: number) => void
  clickCallback?: (
    item: number,
    event: React.KeyboardEvent | React.MouseEvent
  ) => void
  renderCustomContent?: (item: Image | Video) => React.ReactNode
  hasFirstImagePriority?: boolean
}

type CarouselEventHandlers = {
  handleMouseDown: (
    e: React.MouseEvent<HTMLUListElement> | React.TouchEvent<HTMLUListElement>
  ) => void
  handleMouseMove: (
    e: React.MouseEvent<HTMLUListElement> | React.TouchEvent<HTMLUListElement>
  ) => void
  handleMouseUp: (
    e: React.MouseEvent<HTMLUListElement> | React.TouchEvent<HTMLUListElement>
  ) => void
  handleMouseLeave: (
    e: React.MouseEvent<HTMLUListElement> | React.TouchEvent<HTMLUListElement>
  ) => void
  handleKeyDown: (e: React.KeyboardEvent) => void
}

type RenderCarouselImageParams = {
  img: Image
  width: number
  height: number
  sizes?: string
  srcSetUnit?: 'x' | 'w'
  priority?: boolean
}

function renderCarouselImage({
  img,
  width,
  height,
  sizes,
  srcSetUnit,
  priority,
}: RenderCarouselImageParams) {
  return (
    <Image
      src={img.imageUrl}
      alt={img?.imageAlt ?? 'Imagem do carrossel'}
      title={img?.imageAlt ?? img?.imageText ?? ''}
      width={width}
      height={height}
      sizes={sizes}
      srcSetUnit={srcSetUnit}
      fetchpriority={priority ? 'high' : 'auto'}
      loading={priority ? 'eager' : 'lazy'}
      className="w-full h-full object-contain mix-blend-multiply"
    />
  )
}

function useArrowsAndVideo({
  arrows,
  items,
  currentIndex,
  isMobile,
}: {
  arrows: boolean
  items: CarouselItems
  currentIndex: number
  isMobile: boolean
}) {
  const [showArrows, setShowArrows] = useState<boolean>(!!arrows)
  const [isShowingVideo, setIsShowingVideo] = useState<boolean>(false)

  useEffect(() => {
    const showArrowsOnMobile = isMobile && 'url' in (items[currentIndex] ?? {})

    setShowArrows(showArrowsOnMobile ? true : !!arrows)

    setIsShowingVideo(!!showArrowsOnMobile)
  }, [arrows, currentIndex, isMobile, items])

  return { showArrows, isShowingVideo }
}

function getCarouselEventHandlers({
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseLeave,
  handleKeyDown,
}: CarouselEventHandlers) {
  return {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseLeave,
    onTouchStart: handleMouseDown,
    onTouchMove: handleMouseMove,
    onTouchEnd: handleMouseUp,
    onKeyDown: handleKeyDown,
  }
}

const Carousel = forwardRef(
  (
    {
      items,
      width,
      height,
      slideWidthPercentage = 100,
      alignLastSlideToEnd = false,
      sizes,
      srcSetUnit,
      dots = false,
      dotsVariant = 'default',
      arrows = true,
      arrowStyle = 'default',
      enableDrag = true,
      initialSlide,
      clickCallback,
      href,
      Flags,
      onSlideChange,
      sectionClassName,
      renderCustomContent,
      hasFirstImagePriority = false,
    }: CarouselProps,
    ref
  ) => {
    const {
      currentIndex,
      setCurrentIndex,
      isDragging,
      setIsDragging,
      dragStartX,
      setDragStartX,
      dragStartY,
      setDragStartY,
      dragDistance,
      setDragDistance,
      hasDragged,
    } = useCarouselState()

    const { isMobile = false } = useMobile() || {}
    const totalSlides = items.length
    const slideWidth =
      slideWidthPercentage > 0 && slideWidthPercentage <= 100
        ? slideWidthPercentage
        : 100

    const carouselDragProps = {
      enableDrag,
      totalSlides,
      currentIndex,
      setCurrentIndex,
      setIsDragging,
      dragStartX,
      setDragStartX,
      isDragging,
      hasDragged,
      setDragDistance,
      dragStartY,
      setDragStartY,
      dragDistance,
    }

    const {
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleMouseLeave,
      showSlide,
    } = useCarouselDrag(carouselDragProps)

    const { showArrows, isShowingVideo } = useArrowsAndVideo({
      arrows,
      items,
      currentIndex,
      isMobile: !!isMobile,
    })

    const carouselInnerRef = useRef<HTMLUListElement>(null)

    useImperativeHandle(ref, () => ({
      showSlide,
      currentIndex,
    }))

    const handleClick = (
      slideIndex: number,
      event: React.KeyboardEvent | React.MouseEvent<HTMLElement>
    ) => {
      if (hasDragged?.current) {
        event.preventDefault()
        hasDragged.current = false

        return
      }

      if (typeof clickCallback === 'function') {
        clickCallback(slideIndex, event)
      }
    }

    const handleNext = useCallback(() => {
      showSlide(currentIndex + 1)
    }, [currentIndex, showSlide])

    const handlePrev = useCallback(() => {
      showSlide(currentIndex - 1)
    }, [currentIndex, showSlide])

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev()
      } else if (e.key === 'ArrowRight') {
        handleNext()
      }
    }

    const getCarouselStyle = () => {
      const isLastSlide = totalSlides > 0 && currentIndex === totalSlides - 1
      const shouldAlignLastSlideToEnd =
        alignLastSlideToEnd && slideWidth < 100 && isLastSlide

      const endAlignmentOffset = shouldAlignLastSlideToEnd
        ? 100 - slideWidth
        : 0

      const translateX = currentIndex * slideWidth - endAlignmentOffset
      const dragOffset = isDragging ? dragDistance : 0

      return {
        transform: `translateX(calc(-${translateX}% + ${dragOffset}px))`,
        transition: isDragging ? 'none' : 'transform 0.3s ease',
      }
    }

    useEffect(() => {
      if (typeof onSlideChange === 'function') {
        onSlideChange(currentIndex)
      }
    }, [currentIndex, onSlideChange])

    useEffect(() => {
      if (typeof initialSlide === 'number') {
        setCurrentIndex(initialSlide)
      }
    }, [initialSlide, setCurrentIndex])

    return (
      <section
        className={
          sectionClassName ? `carousel ${sectionClassName}` : 'carousel'
        }
        aria-live="polite"
        aria-label="Carrossel de imagens"
      >
        <ul
          className="carousel-inner cursor-zoom-in touch-pan-y"
          ref={carouselInnerRef}
          style={getCarouselStyle()}
          {...getCarouselEventHandlers({
            handleMouseDown,
            handleMouseMove,
            handleMouseUp,
            handleMouseLeave,
            handleKeyDown,
          })}
          role="presentation"
          aria-roledescription="carousel"
        >
          {items?.map((item, index) => (
            <CarouselSlide
              key={index}
              item={item}
              index={index}
              slideWidth={slideWidth}
              currentIndex={currentIndex}
              totalSlides={totalSlides}
              renderImage={(img: Image) =>
                renderCarouselImage({
                  img,
                  width,
                  height,
                  sizes,
                  srcSetUnit,
                  priority: hasFirstImagePriority && index === 0,
                })
              }
              handleClick={handleClick}
              renderCustomContent={renderCustomContent}
              href={href}
              Flags={Flags}
            />
          ))}
        </ul>
        <CarouselControls
          arrows={showArrows}
          dots={dots}
          dotsVariant={dotsVariant}
          items={items}
          currentIndex={currentIndex}
          handleNext={handleNext}
          handlePrev={handlePrev}
          showSlide={showSlide}
          arrowStyle={arrowStyle}
          isShowingVideo={isShowingVideo}
        />
      </section>
    )
  }
)

Carousel.displayName = 'Carousel'

export default Carousel
